import React from 'react';
import { DropletIcon, Lightbulb, ShieldAlert, Palette, Home, Layers } from 'lucide-react';

/**
 * Компонент отображения рекомендаций по выбору краски
 */
const PaintRecommendations = ({ results, roomType, surfaceType, paintType }) => {
  if (!results) return null;

  // Функция для получения рекомендаций по типу комнаты
  const getRoomTypeRecommendations = () => {
    switch (roomType) {
      case 'living':
        return {
          title: 'Для жилой комнаты',
          recommendations: [
            'Используйте краски с матовым или полуматовым финишем для стен',
            'Для потолка выбирайте белую матовую краску с высокой укрывистостью',
            'Рекомендуемые виды краски: водоэмульсионная, акриловая, латексная'
          ]
        };
      case 'kitchen':
        return {
          title: 'Для кухни',
          recommendations: [
            'Выбирайте влагостойкие краски с защитой от плесени и грибка',
            'Полуглянцевые или полуматовые краски лучше очищаются от жира и загрязнений',
            'Рекомендуемые виды краски: латексная, силиконовая, акриловая с антибактериальными добавками'
          ]
        };
      case 'bathroom':
        return {
          title: 'Для ванной комнаты',
          recommendations: [
            'Используйте только влагостойкие краски с защитой от плесени',
            'Полуглянцевые или глянцевые покрытия устойчивы к влаге и конденсату',
            'Рекомендуемые виды краски: латексная, силиконовая, алкидная'
          ]
        };
      case 'hallway':
        return {
          title: 'Для коридора/прихожей',
          recommendations: [
            'Выбирайте износостойкие краски, устойчивые к частым касаниям',
            'Полуматовые или полуглянцевые покрытия лучше моются',
            'Рекомендуемые виды краски: латексная, акриловая повышенной прочности'
          ]
        };
      case 'children':
        return {
          title: 'Для детской комнаты',
          recommendations: [
            'Используйте экологичные краски без запаха и вредных веществ',
            'Выбирайте моющиеся покрытия для легкой очистки от загрязнений',
            'Рекомендуемые виды краски: водоэмульсионная, акриловая (гипоаллергенная)'
          ]
        };
      case 'balcony':
        return {
          title: 'Для балкона/лоджии',
          recommendations: [
            'Используйте атмосферостойкие краски для защиты от перепадов температур',
            'Для внешних частей выбирайте фасадные краски устойчивые к УФ-излучению',
            'Рекомендуемые виды краски: акриловая, силиконовая, фасадная'
          ]
        };
      case 'office':
        return {
          title: 'Для домашнего офиса',
          recommendations: [
            'Выбирайте краски с матовым покрытием, чтобы избежать бликов',
            'Используйте нейтральные оттенки для повышения концентрации',
            'Рекомендуемые виды краски: акриловая, латексная'
          ]
        };
      default:
        return {
          title: 'Общие рекомендации',
          recommendations: [
            'Выбирайте краску в зависимости от типа поверхности и условий эксплуатации',
            'Обратите внимание на экологичность и безопасность продукта',
            'Для внутренних работ предпочтительны водно-дисперсионные краски'
          ]
        };
    }
  };

  // Функция для получения рекомендаций по типу поверхности
  const getSurfaceTypeRecommendations = () => {
    switch (surfaceType) {
      case 'drywall':
        return {
          title: 'Для гипсокартона',
          recommendations: [
            'Обязательно используйте грунтовку для выравнивания впитываемости',
            'Акриловые краски хорошо подходят для гипсокартона',
            'При первичной окраске может потребоваться дополнительный слой'
          ]
        };
      case 'plaster':
        return {
          title: 'Для штукатурки',
          recommendations: [
            'Новая штукатурка должна полностью высохнуть перед покраской (до 28 дней)',
            'Используйте глубоко проникающую грунтовку перед покраской',
            'Паропроницаемые краски позволят стенам "дышать"'
          ]
        };
      case 'concrete':
        return {
          title: 'Для бетона',
          recommendations: [
            'Бетон должен полностью высохнуть и выстояться перед покраской',
            'Используйте специальные грунты для бетонных поверхностей',
            'Выбирайте краски с хорошей адгезией к минеральным основаниям'
          ]
        };
      case 'brick':
        return {
          title: 'Для кирпича',
          recommendations: [
            'Кирпичная поверхность требует предварительной обработки и грунтования',
            'Используйте паропроницаемые краски, особенно для наружных работ',
            'Силикатные и силиконовые краски хорошо подходят для кирпича'
          ]
        };
      case 'wood':
        return {
          title: 'Для дерева',
          recommendations: [
            'Используйте специальные грунты для деревянных поверхностей',
            'Алкидные и масляные краски хорошо защищают древесину',
            'Для наружных работ выбирайте краски с УФ-защитой и защитой от влаги'
          ]
        };
      case 'metal':
        return {
          title: 'Для металла',
          recommendations: [
            'Обязательно используйте антикоррозийный грунт',
            'Алкидные и эпоксидные краски обеспечивают хорошую защиту',
            'Для наружных работ выбирайте краски с повышенной устойчивостью к атмосферным воздействиям'
          ]
        };
      case 'wallpaper':
        return {
          title: 'Для обоев под покраску',
          recommendations: [
            'Используйте водно-дисперсионные краски для сохранения текстуры обоев',
            'Наносите краску в два слоя для равномерного покрытия',
            'Выбирайте валик с ворсом, соответствующим фактуре обоев'
          ]
        };
      default:
        return {
          title: 'Общие рекомендации по подготовке поверхности',
          recommendations: [
            'Тщательно очистите поверхность от загрязнений и старой краски',
            'Используйте подходящую грунтовку для улучшения адгезии',
            'Заделайте все трещины и дефекты перед покраской'
          ]
        };
    }
  };

  // Функция для получения рекомендаций по типу краски
  const getPaintTypeRecommendations = () => {
    switch (paintType) {
      case 'water':
        return {
          title: 'Водоэмульсионная краска',
          recommendations: [
            'Экологичная и безопасная, без сильного запаха',
            'Хорошо подходит для жилых помещений',
            'Быстро сохнет, инструменты легко моются водой'
          ]
        };
      case 'acrylic':
        return {
          title: 'Акриловая краска',
          recommendations: [
            'Универсальна для большинства внутренних поверхностей',
            'Образует эластичное покрытие, устойчивое к растрескиванию',
            'Обладает хорошей влагостойкостью и устойчивостью к загрязнениям'
          ]
        };
      case 'latex':
        return {
          title: 'Латексная краска',
          recommendations: [
            'Повышенная влагостойкость и стойкость к мытью',
            'Хорошо подходит для ванных комнат и кухонь',
            'Образует прочное покрытие с хорошими защитными свойствами'
          ]
        };
      case 'silicone':
        return {
          title: 'Силиконовая краска',
          recommendations: [
            'Высокая паропроницаемость позволяет стенам "дышать"',
            'Отличная водоотталкивающая способность',
            'Долговечное покрытие с высокой устойчивостью к загрязнениям'
          ]
        };
      case 'alkyd':
        return {
          title: 'Алкидная краска',
          recommendations: [
            'Образует прочное покрытие с высоким блеском',
            'Хорошо подходит для дерева и металла',
            'Повышенная износостойкость и влагостойкость'
          ]
        };
      case 'oil':
        return {
          title: 'Масляная краска',
          recommendations: [
            'Традиционный материал с хорошей кроющей способностью',
            'Образует прочное покрытие, устойчивое к истиранию',
            'Длительное время высыхания, сильный запах'
          ]
        };
      case 'chalk':
        return {
          title: 'Меловая краска',
          recommendations: [
            'Создает привлекательную матовую поверхность',
            'Хорошо подходит для декоративной отделки и мебели',
            'Экологичная, но требует дополнительной защиты лаком'
          ]
        };
      case 'mineral':
        return {
          title: 'Минеральная краска',
          recommendations: [
            'Высокая паропроницаемость и экологичность',
            'Подходит для минеральных поверхностей (бетон, штукатурка)',
            'Устойчива к атмосферным воздействиям, хороша для фасадов'
          ]
        };
      case 'silicate':
        return {
          title: 'Силикатная краска',
          recommendations: [
            'Химически связывается с минеральной поверхностью',
            'Очень долговечная, устойчива к атмосферным воздействиям',
            'Идеальна для фасадов, высокая паропроницаемость'
          ]
        };
      default:
        return {
          title: 'Общие рекомендации по выбору краски',
          recommendations: [
            'Выбирайте краску в соответствии с типом помещения и условиями эксплуатации',
            'Обратите внимание на время высыхания и расход краски',
            'Чем выше качество краски, тем долговечнее и привлекательнее будет результат'
          ]
        };
    }
  };

  const roomRecommendations = getRoomTypeRecommendations();
  const surfaceRecommendations = getSurfaceTypeRecommendations();
  const paintRecommendations = getPaintTypeRecommendations();

  // Советы по технике покраски
  const paintingTips = [
    'Начинайте покраску с верхней части стен и потолка, двигаясь вниз',
    'Используйте валик с подходящим ворсом для вашего типа краски и поверхности',
    'Для лучшего результата наносите краску тонкими слоями, давая каждому слою высохнуть',
    'Края и углы прокрашивайте кистью перед использованием валика',
    'Покраску лучше производить при температуре 15-25°C и нормальной влажности'
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Lightbulb className="w-5 h-5 mr-2 text-amber-500" />
        Рекомендации по покраске
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Рекомендации по типу помещения */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 p-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
            <Home className="w-4 h-4 mr-2 text-blue-500" />
            {roomRecommendations.title}
          </h4>
          <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
            {roomRecommendations.recommendations.map((rec, index) => (
              <li key={index} className="flex items-start">
                <span className="text-blue-500 mr-2">•</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Рекомендации по типу поверхности */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800 p-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
            <Layers className="w-4 h-4 mr-2 text-green-500" />
            {surfaceRecommendations.title}
          </h4>
          <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
            {surfaceRecommendations.recommendations.map((rec, index) => (
              <li key={index} className="flex items-start">
                <span className="text-green-500 mr-2">•</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Рекомендации по типу краски */}
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800 p-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
            <DropletIcon className="w-4 h-4 mr-2 text-purple-500" />
            {paintRecommendations.title}
          </h4>
          <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
            {paintRecommendations.recommendations.map((rec, index) => (
              <li key={index} className="flex items-start">
                <span className="text-purple-500 mr-2">•</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Техника покраски */}
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800 p-4">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
            <Palette className="w-4 h-4 mr-2 text-amber-500" />
            Техника покраски
          </h4>
          <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
            {paintingTips.map((tip, index) => (
              <li key={index} className="flex items-start">
                <span className="text-amber-500 mr-2">•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Предупреждение о безопасности */}
      <div className="mt-6 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800 p-4">
        <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
          <ShieldAlert className="w-4 h-4 mr-2 text-red-500" />
          Меры безопасности
        </h4>
        <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li className="flex items-start">
            <span className="text-red-500 mr-2">•</span>
            <span>Обеспечьте хорошую вентиляцию помещения при покраске и в течение нескольких дней после</span>
          </li>
          <li className="flex items-start">
            <span className="text-red-500 mr-2">•</span>
            <span>Используйте средства индивидуальной защиты: перчатки, респиратор, защитные очки</span>
          </li>
          <li className="flex items-start">
            <span className="text-red-500 mr-2">•</span>
            <span>Храните краски в недоступном для детей месте</span>
          </li>
          <li className="flex items-start">
            <span className="text-red-500 mr-2">•</span>
            <span>Утилизируйте остатки краски и тару согласно местным правилам обращения с отходами</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PaintRecommendations;